import React, { useState } from 'react';
import {
  Drawer,
  SwipeableDrawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Tooltip,
  Collapse,
  Chip,
  Button,
} from '@mui/material';
import { ExpandLess, ExpandMore, Add as AddIcon } from '@mui/icons-material';
import { Badge } from '@mui/material';
import { MenuItem } from '../../types';
import { drisCorporateColors } from '../../theme/colors';
import { useCommunicationCounts } from '../../hooks/useCommunicationCounts';

interface SidebarProps {
  open: boolean;
  selectedMenu: string;
  onMenuSelect: (menuId: string) => void;
  menuItems: MenuItem[];
  userEmail?: string;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  currentWidth?: number;
  onToggle?: () => void;
  /**
   * When provided, renders the persistent "Submit a sample" action between the logo and the nav
   * (External portal, new IA — Layout passes it only for External + billingEnabled on desktop; on
   * mobile Layout renders a FAB instead). Absent ⇒ nothing is rendered (staff/partner shells).
   */
  onSubmitSample?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  open,
  selectedMenu,
  onMenuSelect,
  menuItems,
  userEmail,
  isMobile = false,
  mobileOpen = false,
  onMobileClose,
  currentWidth = 280,
  onToggle: _onToggle,
  onSubmitSample,
}) => {
  const theme = useTheme();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  // Live "actionable items" badge for the Communication entries. Consumed HERE (a leaf) rather than
  // prop-threaded from AppLayout so a 60s count poll never re-renders the whole layout tree.
  const { counts } = useCommunicationCounts();
  const communicationTotal = (counts?.requestedBookings ?? 0) + (counts?.newMessages ?? 0);
  // newIntake/newFiles/newSignups are internal-only (the partner counts endpoint omits them) — added
  // to the internal Communication badge sum ONLY; the partner badge stays bookings + messages.
  const internalCommunicationTotal =
    communicationTotal +
    (counts?.newIntake ?? 0) +
    (counts?.newFiles ?? 0) +
    (counts?.newSignups ?? 0);
  const badgeFor = (menuId: string): number =>
    menuId === 'communication'
      ? internalCommunicationTotal
      : menuId === 'partner-communication'
        ? communicationTotal
        : 0;

  const handleMenuSelect = (menuId: string) => {
    onMenuSelect(menuId);
    // Auto-close mobile drawer after selection
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const handleMenuToggle = (menuId: string, hasChildren: boolean, hasPath: boolean) => {
    if (hasChildren) {
      // Toggle expansion for parent items
      setExpandedMenus(prev =>
        prev.includes(menuId)
          ? prev.filter(id => id !== menuId)
          : [...prev, menuId]
      );
      // If parent has a path, also navigate
      if (hasPath) {
        handleMenuSelect(menuId);
      }
    } else {
      // Navigate for items without children
      handleMenuSelect(menuId);
    }
  };

  const sidebarContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo Section */}
      <Box sx={{
        p: isMobile ? 2 : (open ? 1 : 1),
        textAlign: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderBottom: '1px solid rgba(0,0,0,0.12)',
        transition: 'padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        'html.dark &': {
          backgroundColor: 'transparent',
          borderBottom: '1px solid rgba(255,255,255,0.15)',
        },
      }}>
        {/* Light mode logo */}
        <Box
          component="img"
          src={isMobile || open ? "/Artboard 1.png" : "/DRIS-SQ.png"}
          alt="DRIS Logo"
          sx={{
            width: isMobile || open ? '90%' : '45px',
            height: isMobile || open ? 'auto' : '45px',
            objectFit: 'contain',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'block',
            'html.dark &': { display: 'none' },
          }}
        />
        {/* Dark mode logo (white variant) */}
        <Box
          component="img"
          src={isMobile || open ? "/DRIS-WT-LS.png" : "/DRIS-WT-SQ.png"}
          alt="DRIS Logo"
          sx={{
            width: isMobile || open ? '90%' : '45px',
            height: isMobile || open ? 'auto' : '45px',
            objectFit: 'contain',
            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            display: 'none',
            'html.dark &': { display: 'block' },
          }}
        />
      </Box>

      {/* Persistent "Submit a sample" action (External portal, new IA) — design parts.mjs submitCta:
          white, primary text, 12px radius, soft shadow. Collapsed rail shows the icon only. */}
      {onSubmitSample && (
        <Box sx={{ px: open || isMobile ? 2 : 1, pt: 2 }}>
          <Tooltip title={!open && !isMobile ? 'Submit a sample' : ''} placement="right" arrow>
            <Button
              fullWidth
              variant="contained"
              onClick={onSubmitSample}
              aria-label="Submit a sample"
              startIcon={open || isMobile ? <AddIcon /> : undefined}
              sx={{
                minHeight: 44,
                minWidth: 0,
                px: open || isMobile ? 2 : 0,
                borderRadius: 1.5,
                bgcolor: '#FFFFFF',
                color: drisCorporateColors.darkGreen,
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                '&:hover': { bgcolor: '#FFFFFF', boxShadow: '0 6px 12px rgba(0,0,0,0.2)' },
              }}
            >
              {open || isMobile ? 'Submit a sample' : <AddIcon />}
            </Button>
          </Tooltip>
        </Box>
      )}

      {/* Navigation Menu */}
      <List sx={{ flex: 1, pt: 2, overflow: 'auto' }}>
        {menuItems.filter((item) => !item.hidden).map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isExpanded = expandedMenus.includes(item.id);
          const hasPath = !!item.path;

          return (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <Tooltip
                  title={!open && !isMobile ? item.label : ''}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    onClick={() => handleMenuToggle(item.id, hasChildren, hasPath)}
                    selected={selectedMenu === item.id}
                    sx={{
                      mx: 1,
                      mb: hasChildren && isExpanded ? 0.5 : 1,
                      borderRadius: 2,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      minHeight: 48,
                      justifyContent: open || isMobile ? 'initial' : 'center',
                    '&.Mui-selected': {
                      background: 'rgba(255,255,255,0.9)',
                      color: drisCorporateColors.darkGreen,
                      fontWeight: 600,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                      border: '1px solid rgba(255,255,255,0.3)',
                      '&:hover': {
                        background: '#ffffff',
                        transform: 'translateX(4px)',
                      },
                      '& .MuiListItemIcon-root': {
                        color: drisCorporateColors.darkGreen,
                      },
                      '& .MuiListItemText-primary': {
                        fontWeight: 600,
                      },
                    },
                    '&:hover': {
                      background: 'rgba(255,255,255,0.15)',
                      transform: 'translateX(2px)',
                    },
                    }}
                  >
                    <ListItemIcon sx={{
                      color: 'white',
                      minWidth: isMobile ? 56 : (open ? 40 : 0),
                      justifyContent: 'center',
                      mr: open || isMobile ? 0 : 0,
                    }}>
                      {badgeFor(item.id) > 0 ? (
                        <Badge badgeContent={badgeFor(item.id)} color="error" max={99}>
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {(open || isMobile) && (
                      <>
                        <ListItemText
                          primary={item.label}
                          slotProps={{
                            primary: {
                              fontSize: isMobile ? '1rem' : '0.9rem',
                              fontWeight: selectedMenu === item.id ? 600 : 400
                            }
                          }}
                        />
                        {hasChildren && (
                          isExpanded ? <ExpandLess /> : <ExpandMore />
                        )}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
              {/* Render children */}
              {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children!.map((child) => (
                      <ListItem key={child.id} disablePadding>
                        <Tooltip
                          title={!open && !isMobile ? child.label : ''}
                          placement="right"
                          arrow
                        >
                          <ListItemButton
                            onClick={() => handleMenuSelect(child.id)}
                            selected={selectedMenu === child.id}
                            sx={{
                              mx: 1,
                              mb: 1,
                              ml: 2,
                              pl: (open || isMobile) ? 4 : 1,
                              borderRadius: 2,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              minHeight: 44,
                              justifyContent: open || isMobile ? 'initial' : 'center',
                            '&.Mui-selected': {
                              background: 'rgba(255,255,255,0.9)',
                              color: drisCorporateColors.darkGreen,
                              fontWeight: 600,
                              boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                              border: '1px solid rgba(255,255,255,0.3)',
                              '&:hover': {
                                background: '#ffffff',
                                transform: 'translateX(4px)',
                              },
                              '& .MuiListItemIcon-root': {
                                color: drisCorporateColors.darkGreen,
                              },
                              '& .MuiListItemText-primary': {
                                fontWeight: 600,
                              },
                            },
                            '&:hover': {
                              background: 'rgba(255,255,255,0.15)',
                              transform: 'translateX(2px)',
                            },
                            }}
                          >
                            <ListItemIcon sx={{
                              color: 'white',
                              minWidth: isMobile ? 48 : (open ? 36 : 0),
                              justifyContent: 'center',
                              mr: open || isMobile ? 0 : 0,
                            }}>
                              {child.icon}
                            </ListItemIcon>
                            {(open || isMobile) && (
                              <ListItemText
                                primary={child.label}
                                slotProps={{
                                  primary: {
                                    fontSize: isMobile ? '0.95rem' : '0.85rem',
                                    fontWeight: selectedMenu === child.id ? 600 : 400
                                  }
                                }}
                              />
                            )}
                          </ListItemButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* User Section */}
      {userEmail && (
        <Box sx={{
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.2)',
          mt: 'auto'
        }}>
          {/* Environment Indicator - only show for non-production environments */}
          {import.meta.env.VITE_ENV &&
           ['staging', 'legacy'].includes(import.meta.env.VITE_ENV.toLowerCase()) && (
            <Chip
              label={import.meta.env.VITE_ENV.toUpperCase()}
              size="small"
              sx={{
                backgroundColor: drisCorporateColors.orange,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24,
                mb: 1,
                display: (open || isMobile) ? 'flex' : 'none',
                width: 'fit-content',
                mx: (open || isMobile) ? 2 : 'auto',
              }}
            />
          )}
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.8rem',
              textAlign: (open || isMobile) ? 'left' : 'center',
              px: (open || isMobile) ? 2 : 0,
              wordBreak: 'break-word'
            }}
          >
            {(open || isMobile) ? userEmail : userEmail.charAt(0).toUpperCase()}
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <SwipeableDrawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onOpen={() => {}}
        onClose={onMobileClose || (() => {})}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: currentWidth,
            background: drisCorporateColors.darkGreen,
            color: 'white',
            border: 'none',
          },
        }}
      >
        {sidebarContent}
      </SwipeableDrawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: currentWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          background: drisCorporateColors.darkGreen,
          color: 'white',
          border: 'none',
          overflow: 'hidden',
        },
      }}
    >
      {sidebarContent}
    </Drawer>
  );
};
